import { useEffect, useRef } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { ProviderProgramAssessmentQuery } from '../../generated/graphql';
import { FormQuestion } from '../lib/program-assessment';
import { CompleteProviderFormData } from '../pages/CompleteProviderForm/helpers';
import { assessmentAnswersToFormValues } from '../lib/formDraftHelpers';

interface UseDraftRestorationOptions<T extends CompleteProviderFormData> {
  programAssessment:
    | ProviderProgramAssessmentQuery['programAssessment']
    | null
    | undefined;
  questions: FormQuestion[] | undefined;
  programAssessmentId: string | undefined;
  reset: UseFormReset<T>;
  setValue?: UseFormSetValue<T>;
  getDefaultValues: () => T;
  isMultiFormMode?: boolean;
  clearForm?: () => void;
}

/**
 * Hook to handle draft restoration for form components.
 *
 * Manages:
 * - Resetting forms when programAssessmentId changes (prevents values persisting across assessments)
 * - Restoring draft answers from GraphQL query
 * - Restoring signature fields (for single form mode)
 * - Tracking restoration state to prevent duplicate restorations
 *
 * @param options - Configuration options for draft restoration
 * @param options.programAssessment - The program assessment data from GraphQL query
 * @param options.questions - The assessment questions for mapping answers
 * @param options.programAssessmentId - The ID of the program assessment
 * @param options.reset - React Hook Form reset function
 * @param options.setValue - Optional React Hook Form setValue function (for multi-form mode)
 * @param options.getDefaultValues - Function that returns default form values
 * @param options.isMultiFormMode - Whether the form is in multi-form mode (default: false)
 * @param options.clearForm - Optional function to clear persisted form data (for client portal)
 *
 * @example
 * ```tsx
 * useDraftRestoration({
 *   programAssessment,
 *   questions,
 *   programAssessmentId,
 *   reset,
 *   setValue,
 *   getDefaultValues,
 *   isMultiFormMode,
 * });
 * ```
 */
export const useDraftRestoration = <T extends CompleteProviderFormData>({
  programAssessment,
  questions,
  programAssessmentId,
  reset,
  setValue,
  getDefaultValues,
  isMultiFormMode = false,
  clearForm,
}: UseDraftRestorationOptions<T>): void => {
  // Track previous programAssessmentId to only reset when it actually changes
  const previousProgramAssessmentIdRef = useRef<string | undefined>(undefined);
  const restoredDraftForAssessmentIdRef = useRef<string | undefined>(undefined);

  // Reset form when programAssessmentId changes to prevent values from persisting across different assessments
  useEffect(() => {
    // Only reset if programAssessmentId has actually changed (not on initial mount with same ID)
    if (
      previousProgramAssessmentIdRef.current !== undefined &&
      previousProgramAssessmentIdRef.current !== programAssessmentId
    ) {
      reset(getDefaultValues());
      // Clear persisted form data when switching to a different assessment
      if (clearForm) {
        clearForm();
      }
      restoredDraftForAssessmentIdRef.current = undefined; // Allow restoring drafts for new assessment
    }
    previousProgramAssessmentIdRef.current = programAssessmentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programAssessmentId, reset, getDefaultValues, clearForm]);

  // Restore draft answers or saved answers when form loads (only once per programAssessmentId)
  useEffect(() => {
    if (
      programAssessment &&
      questions &&
      questions.length > 0 &&
      programAssessmentId &&
      restoredDraftForAssessmentIdRef.current !== programAssessmentId
    ) {
      const hasDraftAnswers =
        programAssessment.draftAnswers &&
        programAssessment.draftAnswers.length > 0;
      const hasSavedAnswers =
        programAssessment.answers && programAssessment.answers.length > 0;
      const hasDraftSignatureFields =
        programAssessment.draftProviderSignatureName ||
        programAssessment.draftProviderSignatureRole ||
        programAssessment.draftSignatureTermsAgreed !== undefined;

      // Use saved answers when available (from any signer - e.g. when Team Member/Sender signs first).
      // Fall back to draft answers for in-progress forms where no one has signed yet.
      const answersToRestore = hasSavedAnswers
        ? programAssessment.answers
        : hasDraftAnswers
        ? programAssessment.draftAnswers
        : null;

      // Restore if we have answers to restore OR draft signature fields
      if (answersToRestore || hasDraftSignatureFields) {
        const restoredAnswers = answersToRestore
          ? assessmentAnswersToFormValues(answersToRestore, questions)
          : [];

        // Ensure we have an answer for each question (pad with empty values if needed)
        const paddedAnswers = questions.map(
          (_, idx) =>
            restoredAnswers[idx] || {
              value: '',
              values: [],
              contactId: undefined,
            },
        );

        if (isMultiFormMode && setValue && programAssessmentId) {
          // In multi-form mode, use setValue to set answers at the correct nested path
          // (forms.${assessmentId}.answers) instead of top-level answers
          paddedAnswers.forEach((answer, idx) => {
            if (answer.value !== undefined) {
              setValue(
                `forms.${programAssessmentId}.answers.${idx}.value` as any,
                answer.value as any,
              );
            }
            if (answer.values && answer.values.length > 0) {
              setValue(
                `forms.${programAssessmentId}.answers.${idx}.values` as any,
                answer.values as any,
              );
            }
          });

          // Restore signature fields for this specific form
          if (hasDraftSignatureFields) {
            if (programAssessment.draftProviderSignatureName) {
              setValue(
                `forms.${programAssessmentId}.providerSignatureName` as any,
                programAssessment.draftProviderSignatureName as any,
              );
            }
            if (programAssessment.draftProviderSignatureRole) {
              setValue(
                `forms.${programAssessmentId}.providerSignatureRole` as any,
                programAssessment.draftProviderSignatureRole as any,
              );
            }
            if (programAssessment.draftSignatureTermsAgreed !== undefined) {
              setValue(
                `forms.${programAssessmentId}.signatureTermsAgreed` as any,
                programAssessment.draftSignatureTermsAgreed as any,
              );
            }
          }
        } else {
          // Single form mode: reset entire form with restored data
          const defaultValues = getDefaultValues();
          const restoredFormData = {
            ...defaultValues,
            answers: paddedAnswers,
          } as T;

          // Restore signature fields if available (single form mode only)
          if (hasDraftSignatureFields) {
            if (programAssessment.draftProviderSignatureName) {
              (
                restoredFormData as CompleteProviderFormData
              ).providerSignatureName =
                programAssessment.draftProviderSignatureName;
            }
            if (programAssessment.draftProviderSignatureRole) {
              (
                restoredFormData as CompleteProviderFormData
              ).providerSignatureRole =
                programAssessment.draftProviderSignatureRole;
            }
            if (programAssessment.draftSignatureTermsAgreed !== undefined) {
              (
                restoredFormData as CompleteProviderFormData
              ).signatureTermsAgreed =
                programAssessment.draftSignatureTermsAgreed;
            }
          }

          reset(restoredFormData);
        }

        restoredDraftForAssessmentIdRef.current = programAssessmentId;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    programAssessment?.draftAnswers,
    programAssessment?.answers,
    programAssessment?.completedAt,
    programAssessment?.draftProviderSignatureName,
    programAssessment?.draftProviderSignatureRole,
    programAssessment?.draftSignatureTermsAgreed,
    questions,
    programAssessmentId,
    isMultiFormMode,
    reset,
    getDefaultValues,
  ]);
};
